import { useState, useEffect, useMemo, useCallback } from "react";
import { EMPTY_LINE_ITEM } from "../constants";
import {
  generateId,
  calculateTaxBreakdown,
  resolveBillingEntity,
  generateLineItemsFromBooking,
} from "../utils";
import { getFinalBookingTotal } from "../../../../../utils";

export const useBillingData = (bookingDetails, gstEnabled) => {
  const [isEditing, setIsEditing] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    guestName: "",
    guestAddress: "",
    guestEmail: "",
    guestMobile: "",
    guestGstin: "",
    invoiceDate: new Date(),
    invoiceNumber: "",
  });
  const [lineItems, setLineItems] = useState([]);

  const grandTotal = useMemo(() => {
    return lineItems
      .reduce((acc, item) => acc + (parseFloat(item.inclusiveAmount) || 0), 0)
      .toFixed(2);
  }, [lineItems]);

  const hasFinalBill = useMemo(() => {
    return Boolean(bookingDetails?.invoice_id);
  }, [bookingDetails?.invoice_id]);

  const finalBillSummary = useMemo(() => {
    const total =getFinalBookingTotal(bookingDetails);
    const paid = bookingDetails?.paid_amount || 0;
    return {
      total,
      paid,
      pending: total - paid,
    };
  }, [
    bookingDetails?.custom_price,
    bookingDetails?.total_price,
    bookingDetails?.paid_amount,
  ]);

  useEffect(() => {
    if (!bookingDetails) return;

    const billingEntity = resolveBillingEntity(bookingDetails);
    setInvoiceData({
      guestName: billingEntity.name,
      guestAddress: billingEntity.address,
      guestEmail: billingEntity.email,
      guestMobile: billingEntity.mobile,
      guestGstin: billingEntity.gstin,
      invoiceDate: new Date(),
      invoiceNumber: `PI-${bookingDetails.booking_id}`,
    });

    const items = generateLineItemsFromBooking(bookingDetails, gstEnabled);
    setLineItems(items);
  }, [bookingDetails, gstEnabled]);

  const handleHeaderChange = useCallback((e) => {
    const { name, value } = e.target;
    setInvoiceData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleLineItemChange = useCallback((index, field, value) => {
    setLineItems((prevItems) => {
      const newItems = [...prevItems];
      newItems[index] = { ...newItems[index], [field]: value };

      if (field === "inclusiveAmount" || field === "qty") {
        const amt = parseFloat(newItems[index].inclusiveAmount) || 0;
        const qty = parseFloat(newItems[index].qty) || 1;
        newItems[index].inclusiveRate = (amt / qty).toFixed(2);
        
        if (gstEnabled) {
          Object.assign(newItems[index], calculateTaxBreakdown(amt, qty));
        } else {
          // No tax calculation - amount is the base
          newItems[index].base = amt.toFixed(2);
          newItems[index].sgst = "0.00";
          newItems[index].cgst = "0.00";
          newItems[index].taxRate = 0;
        }
      }

      return newItems;
    });
  }, [gstEnabled]);

  const addLineItem = useCallback(() => {
    setLineItems((prev) => [...prev, { ...EMPTY_LINE_ITEM, id: generateId() }]);
  }, []);

  const removeLineItem = useCallback((index) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const toggleEditing = useCallback(() => {
    setIsEditing((prev) => !prev);
  }, []);

  return {
    isEditing,
    invoiceData,
    lineItems,
    grandTotal,
    hasFinalBill,
    finalBillSummary,
    handleHeaderChange,
    handleLineItemChange,
    addLineItem,
    removeLineItem,
    toggleEditing,
  };
};